import { Phone, MessageCircle } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import EnquirySection from "../components/EnquirySection";
import { BUSINESS, GALLERY, PILLARS, waLink } from "../lib/constants";
import { usePageTitle } from "../lib/seo";

const PILLAR_ICONS = ["Quality Materials", "Beautiful Decorations", "Timely Arrangements", "Customer Satisfaction"];

const INVENTORY = [
  "Waterproof Pandals",
  "Hangar & Shamiyana Tents",
  "Traditional Mandap Setups",
  "Decorative & LED Lighting",
  "Stage & Seating Arrangements",
  "Entrance Archways",
];

export default function About() {
  usePageTitle("About Us");
  return (
    <div>
      <PageHero
        eyebrow="About Us"
        title="Celebrations Planned with Care."
        sub="A Bilaspur-based tent house and decoration team devoted to making every occasion look beautiful and run smoothly."
        image={GALLERY[4].src}
      />

      {/* Story */}
      <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 md:py-28 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <img
              src={GALLERY[6].src}
              alt="Wedding venue decoration by Jaiswal Tent House"
              loading="lazy"
              className="aspect-[4/5] w-full rounded-2xl border border-gold/20 object-cover"
            />
            <div className="absolute -bottom-5 -right-5 hidden rounded-2xl border border-gold/40 bg-[#14120E] p-6 sm:block">
              <p className="font-display text-lg text-amber-100">Bilaspur,</p>
              <p className="font-display text-lg italic text-gold-grad">Chhattisgarh</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="eyebrow">Our Story</p>
          <h2 className="mt-3 font-display text-3xl font-semibold leading-tight text-amber-100 sm:text-4xl">
            Beautiful arrangements, <span className="text-gold-grad italic">dependable service.</span>
          </h2>
          <div className="mt-6 space-y-4 text-sm leading-relaxed text-stone-300 sm:text-base">
            <p>
              {BUSINESS.name} is a tent house and event decoration service based in {BUSINESS.location}. We
              provide beautiful tent arrangements and decoration services for weddings, receptions,
              birthdays, parties, and all special occasions.
            </p>
            <p>
              Our commitment is simple — quality materials, beautiful decorations, timely arrangements,
              and complete customer satisfaction. From the first site visit to the final takedown, we
              plan every detail with you so your celebration looks exactly the way you imagined it.
            </p>
            <p>
              Whether it is an intimate haldi at home or a grand wedding pandal for the whole
              neighbourhood, we bring the same care, craft and punctuality to every setup.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Pillars */}
      <section className="border-y border-gold/15 bg-[#FAF6EE] py-20 text-[#14120E] md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow !text-gold-deep">What We Stand For</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
              The four things we never compromise on.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="h-full">
                <div className="h-full rounded-2xl border border-[#AA771C]/25 bg-white/60 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(170,119,28,0.18)]">
                  <p className="font-accent text-xs tracking-[0.3em] text-gold-deep">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-xl font-semibold">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone-600">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="sr-only">{PILLAR_ICONS.join(", ")}</p>
        </div>
      </section>

      {/* Inventory */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 md:py-28">
        <Reveal>
          <p className="eyebrow">Our Setup</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-amber-100 sm:text-4xl">
            A ready inventory for <span className="text-gold-grad italic">every kind of occasion</span>
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {INVENTORY.map((item, i) => (
            <Reveal key={item} delay={(i % 3) * 0.07}>
              <div className="card-royal flex items-center justify-between px-6 py-5 transition-all duration-300 hover:-translate-y-1">
                <p className="font-display text-lg text-amber-100">{item}</p>
                <span className="h-1.5 w-1.5 rotate-45 bg-gold" />
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14">
          <div className="card-royal flex flex-col items-center justify-between gap-6 p-8 text-center sm:p-10 md:flex-row md:text-left">
            <div>
              <h3 className="font-display text-2xl text-amber-100 sm:text-3xl">Serving Bilaspur &amp; nearby areas</h3>
              <p className="mt-2 text-sm text-stone-400">
                We set up across Bilaspur city and the surrounding areas of Chhattisgarh.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap justify-center gap-3">
              <a href={BUSINESS.phoneHref} data-testid="about-call-btn" className="btn-gold">
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a
                href={waLink("Hello Jaiswal Tent House! I would like to enquire about your services.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-whatsapp-btn"
                className="btn-outline-gold"
              >
                <MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp Us
              </a>
            </div>
          </div>
        </Reveal>
      </section>

      <EnquirySection />
    </div>
  );
}
